/* import __COLOCATED_TEMPLATE__ from './attribute.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import ConversationAttributeSummary from 'embercom/objects/inbox/conversation-attribute-summary';
import type ConversationAttributeDescriptor from 'embercom/objects/inbox/conversation-attribute-descriptor';
import { DataType } from 'embercom/objects/inbox/conversation-attribute-descriptor';
// @ts-ignore
import { trackedReset } from 'tracked-toolbox';
import { type TicketDescriptor } from 'embercom/objects/inbox/ticket';
import { type QaScorecardEvaluator } from 'embercom/objects/inbox/conversation-table-entry';
import type Session from 'embercom/services/session';
import {
  PER_TARGET_SLA_DESCRIPTOR_IDS,
  DESCRIPTOR_ID_TO_SLA_TARGET_TYPE,
} from 'embercom/objects/inbox/conversations-table-column';

interface Args {
  attribute?: ConversationAttributeSummary;
  descriptor: ConversationAttributeDescriptor | TicketDescriptor;
  showUnavailableWhenMissing?: boolean;
  scorecardEvaluator?: QaScorecardEvaluator;
}

interface Signature {
  Args: Args;
  Element: HTMLParagraphElement;
}

export default class Attribute extends Component<Signature> {
  @service declare session: Session;

  @trackedReset('args.attribute') attribute =
    // @ts-ignore
    this.args.attribute ?? new ConversationAttributeSummary(undefined, this.args.descriptor);

  get isPerTargetSla(): boolean {
    let descriptorId = String(this.args.descriptor.id);
    return (
      this.session.workspace.isFeatureEnabled('per-target-sla-sort-read') &&
      (PER_TARGET_SLA_DESCRIPTOR_IDS.includes(descriptorId) ||
        descriptorId in DESCRIPTOR_ID_TO_SLA_TARGET_TYPE)
    );
  }

  get breachDate(): Date | undefined {
    if (this.isPerTargetSla && this.attribute.value) {
      return new Date(this.attribute.value as string);
    }
    return undefined;
  }

  get hasValue() {
    let value = this.attribute.value;
    return value !== null && value !== undefined;
  }

  get isAiEvaluator() {
    return this.args.scorecardEvaluator?.evaluationType === 'ai';
  }

  get listOptionLabel() {
    let { descriptor } = this.args;

    if (descriptor.type === DataType.List) {
      if (!descriptor.listOptions) {
        return undefined;
      }

      let option = descriptor.listOptions.find((option) => option.id === this.attribute.value);
      if (!option) {
        return undefined;
      }

      return option.label;
    } else {
      return undefined;
    }
  }

  get shouldShowPending() {
    // Display hierarchy: Value > Pending > Unavailable > empty
    if (!this.isAiEvaluator) {
      return false;
    }
    return !this.hasValue;
  }

  get shouldShowUnavailable() {
    if (!this.args.showUnavailableWhenMissing) {
      return false;
    }
    if (this.shouldShowPending) {
      return false;
    }
    return !this.hasValue;
  }

  get value() {
    let { descriptor } = this.args;

    if (descriptor.type === DataType.List) {
      if (this.listOptionLabel !== undefined) {
        return this.listOptionLabel;
      }
      return this.hasValue ? String(this.attribute.value) : undefined;
    }
    return this.attribute.value as string | undefined;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationsTable::TableCells::Attribute': typeof Attribute;
    'inbox2/conversations-table/table-cells/attribute': typeof Attribute;
  }
}
